import type { FSVDrinkOptions, FSVFoodPackages } from "~/types/api-types";

/**
 * Guest-facing food/drink minimum category from admin selections (design matrix).
 * API uses `no_food` for “none” and `non_alcoholic_options_only` for non-alcoholic drinks.
 */
export type FoodDrinkLabel =
  | "food_and_drink_min"
  | "drink_options_only"
  | "food_min_only"
  | "food_and_drink";

const WEAK_FOOD_PACKAGES = new Set<FSVFoodPackages>([
  "outside_food_allowed",
  "no_food",
]);

const isWeakFood = (food: FSVFoodPackages): boolean =>
  WEAK_FOOD_PACKAGES.has(food);

const isWeakDrink = (drink: FSVDrinkOptions): boolean =>
  drink === "outside_drinks_allowed";

/**
 * Label matrix:
 * | Weak food (outside / none) | Weak drink (outside)     | food_and_drink_min  |
 * | Weak food                  | Real drink               | drink_options_only  |
 * | Real food                  | Weak drink               | food_min_only       |
 * | Real food                  | Real drink               | food_and_drink      |
 *
 * Real food = catering_packages[_available] | full_menu_a_la_carte
 * Real drink = full_bar | beer_and_wine_only | non_alcoholic_options_only
 */
export const getFoodDrinkLabel = (
  foodPackage: FSVFoodPackages | undefined,
  drinkOption: FSVDrinkOptions | undefined,
): FoodDrinkLabel | null => {
  if (foodPackage === undefined || drinkOption === undefined) return null;

  const weakFood = isWeakFood(foodPackage);
  const weakDrink = isWeakDrink(drinkOption);

  if (weakFood && weakDrink) return "food_and_drink_min";
  if (weakFood && !weakDrink) return "drink_options_only";
  if (!weakFood && weakDrink) return "food_min_only";

  return "food_and_drink";
};
